import {
    Account,
    Model,
    Order,
    OrderActualTransaction,
    OrderWithExtraInfo,
    SecurityType,
} from '@orbis-systems/orbis-api-middleware';
import { multiply, uniqBy } from 'lodash';

const SELL_TRANSACTIONS = new Set([
    OrderActualTransaction.SellToClose,
    OrderActualTransaction.BuyToClose,
    OrderActualTransaction.Sell,
    OrderActualTransaction.BuyToCover,
]);

function getSecurityType(order: Order): SecurityType {
    if (order.option) return SecurityType.Option;
    if (order.mutualFund) return SecurityType.MutualFund;
    if (order.bond) return SecurityType.Bond;
    return SecurityType.Equity;
}

function getTradeType(order: Order, isInventoryAccount: boolean): string {
    if (order.modelId) return 'Block Trade';
    if (order.allocation) return 'Allocation';
    return isInventoryAccount ? 'Average Price Account' : 'Single Account';
}

export function formatModelOrders({
    orders,
    model,
    inventoryAccount,
    combineOptionLegs = true,
}: {
    orders: Order[];
    model?: Model;
    inventoryAccount?: Account;
    combineOptionLegs?: boolean;
}): OrderWithExtraInfo[] {
    const inventoryId = inventoryAccount?.accountId;
    const uniqueOrders = uniqBy(orders, 'ourRef');

    const processedOrders = uniqueOrders.flatMap((order) => {
        if (order.accountId === inventoryId && order.allocation) {
            return [];
        }

        if (combineOptionLegs && order.orderLegs?.length) {
            return order.orderLegs;
        }

        return [order];
    });

    return processedOrders.map((order) => {
        const { quote, execQty, execPx, actualTransaction } = order;
        const isInventoryAccount = order.accountId === inventoryId;
        const rawValue = quote ? multiply(quote.lastPrice, execQty) : 0;

        return {
            ...order,
            gain: SELL_TRANSACTIONS.has(actualTransaction) ? order.profitLoss : order.gain,
            companyName: quote?.companyName || '',
            model: model?.title ?? 'Average Price Account',
            fullName: inventoryAccount?.fullName || '',
            accountNumber: inventoryAccount?.accountNumber || '',
            costValue: multiply(execPx, execQty) || 0,
            value: quote?.option ? multiply(rawValue, 100) : rawValue,
            total: multiply(order.expectedPx, order.quantity),
            securityType: getSecurityType(order),
            tradeType: getTradeType(order, isInventoryAccount),
        };
    });
}

export function formatAccountOrders({
    account,
    orders,
    combineOptionLegs = true,
}: {
    orders: Order[];
    account?: Account;
    combineOptionLegs?: boolean;
}): OrderWithExtraInfo[] {
    const uniqueOrders = uniqBy(orders, 'ourRef');

    const processedOrders = uniqueOrders.flatMap((order) => {
        if (combineOptionLegs && order.orderLegs?.length) {
            return order.orderLegs;
        }
        return [order];
    });

    return processedOrders.map((order) => {
        const { quote, execQty, execPx, actualTransaction } = order;
        const rawValue = quote ? multiply(quote.lastPrice, execQty) : 0;

        return {
            ...order,
            gain: SELL_TRANSACTIONS.has(actualTransaction as OrderActualTransaction) ? order.profitLoss : order.gain,
            costValue: multiply(execPx, execQty) || 0,
            value: quote?.option ? multiply(rawValue, 100) : rawValue,
            total: multiply(order.expectedPx, order.quantity),
            model: '',
            fullName: account?.fullName || '',
            accountNumber: account?.accountNumber || '',
            companyName: quote?.companyName || '',
            securityType: getSecurityType(order),
            tradeType: getTradeType(order, false),
        };
    });
}
